import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import MasterclassManager from "./pages/Admin/MasterclassManager";
import SessionManager from "./pages/Admin/SessionManager";
import UserManager from "./pages/Admin/UserManager";
import SpeakerManager from "./pages/Admin/SpeakerManager";
import AttendanceManager from "./pages/Admin/AttendanceManager";
import NotificationManager from "./pages/Admin/NotificationManager";
import MessageManager from "./pages/Admin/MessageManager";
import SettingsManager from "./pages/Admin/SettingsManager";
import VenueManager from "./pages/Admin/VenueManager";
import AdminLayout from "./pages/Admin/AdminLayout";
import EventSelector from "./pages/EventSelector";
import AdminCreateEvent from "./pages/Admin/AdminCreateEvent";
import AdminDownloads from "./pages/Admin/AdminDownloads";
import Register from "./pages/Register"; // Add import

// Layout
import Layout from "./components/Layout";

// Pages
import Login from "./pages/Login";
import Home from "./pages/Home";
import Programme from "./pages/Programme";
import SessionDetail from "./pages/SessionDetail";
import Speakers from "./pages/Speakers";
import SpeakerDetail from "./pages/SpeakerDetail";
import MySchedule from "./pages/MySchedule";
import Masterclasses from "./pages/Masterclasses";
import MasterclassDetail from "./pages/MasterclassDetail";
import MyQRCode from "./pages/MyQRCode";
import VenueMap from "./pages/VenueMap";
import Notifications from "./pages/Notifications";
import HelpSupport from "./pages/HelpSupport";
import Profile from "./pages/Profile";
import Survey from "./pages/Survey";
import ExternalSurvey from "./pages/ExternalSurvey";
import VerifyAttendee from "./pages/VerifyAttendee"; // Add import

import "./App.css";

// Protected Route Wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader-logo">
          <img
            src="/logo.png"
            alt="RF Logo"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              padding: "8px",
            }}
          />
        </div>
        <div className="loader-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Simple Admin Route Wrapper
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Simple check - in production use backend verification
  if (!user || !user.is_admin) return <Navigate to="/" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/verify/:registrationId" element={<VerifyAttendee />} />{" "}
      {/* Public Verify Route */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} /> {/* Add Route */}
      {/* 1. Landing Page after Login: List of Events */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <EventSelector />
          </ProtectedRoute>
        }
      />
      {/* 2. Admin Creation Route */}
      <Route path="/admin/create-event" element={<AdminCreateEvent />} />
      <Route
        path="/admin/edit-event/:eventId"
        element={
          <AdminRoute>
            <AdminCreateEvent />
          </AdminRoute>
        }
      />
      {/* 3. Event Specific Routes (The Dashboard) */}
      {/* Notice the URL now includes /event/:eventId */}
      <Route
        path="/event/:eventId"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path="programme" element={<Programme />} />
        <Route path="session/:id" element={<SessionDetail />} />
        <Route path="speakers" element={<Speakers />} />
        <Route path="speaker/:id" element={<SpeakerDetail />} />
        <Route path="my-schedule" element={<MySchedule />} />
        <Route path="masterclasses" element={<Masterclasses />} />
        <Route path="masterclass/:id" element={<MasterclassDetail />} />
        <Route path="my-qr" element={<MyQRCode />} />
        <Route path="venue" element={<VenueMap />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="help" element={<HelpSupport />} />
        <Route path="profile" element={<Profile />} />
        <Route path="feedback" element={<Survey />} />
        <Route path="survey" element={<ExternalSurvey />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }
      >
        {/* Global admin routes (no specific event) */}
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<UserManager />} />
        <Route path="speakers" element={<SpeakerManager />} />
        <Route path="sessions" element={<SessionManager />} />
        <Route path="masterclasses" element={<MasterclassManager />} />
        <Route path="attendance" element={<AttendanceManager />} />
        <Route path="venues" element={<VenueManager />} />
        <Route path="notifications" element={<NotificationManager />} />
        <Route path="messages" element={<MessageManager />} />
        <Route path="settings" element={<SettingsManager />} />
        <Route path="downloads" element={<AdminDownloads />} />

        {/* Scoped admin routes (specific event) */}
        <Route path="event/:eventId" element={<AdminDashboard />} />
        <Route path="event/:eventId/users" element={<UserManager />} />
        <Route path="event/:eventId/speakers" element={<SpeakerManager />} />
        <Route path="event/:eventId/sessions" element={<SessionManager />} />
        <Route path="event/:eventId/venues" element={<VenueManager />} />
        <Route
          path="event/:eventId/masterclasses"
          element={<MasterclassManager />}
        />
        <Route
          path="event/:eventId/attendance"
          element={<AttendanceManager />}
        />
        <Route path="event/:eventId/notifications" element={<NotificationManager />} />
        <Route path="event/:eventId/messages" element={<MessageManager />} />
        <Route path="event/:eventId/settings" element={<SettingsManager />} />
        <Route path="event/:eventId/downloads" element={<AdminDownloads />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppProvider>
          <AppRoutes />
        </AppProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
